import React, {useEffect} from 'react';
import {MobileOS} from './types';
import {useRouter} from 'next/router';

type URLParamsProviderValue = {
	error?: string;
	campaign: string;
	platform: MobileOS;
	authToken: string;
	arl: string;
	lang: string;
	rtl: boolean;
	userId: string;
	startingSlide: number;
};

const URLParamsContext = React.createContext<URLParamsProviderValue | null>(
	null,
);

const defaults: URLParamsProviderValue = {
	platform: MobileOS.NULL,
	campaign: 'mdy',
	authToken: '',
	lang: 'en-US',
	arl: '',
	rtl: false,
	userId: '',
	startingSlide: 0,
};

export const getInitialParams = (): URLParamsProviderValue => {
	if (
		typeof window !== 'undefined' &&
		typeof window?.location !== 'undefined'
	) {
		const params = new URLSearchParams(window.location.search);

		return {
			...defaults,
			...{
				platform: (params.get('platform') as MobileOS) || defaults.platform,
				authToken: params.get('authToken') || defaults.authToken,
				campaign: params.get('campaign') || defaults.campaign,
				lang: params.get('lang') || defaults.lang,
				arl: params.get('arl') || defaults.arl,
				rtl:
					Boolean(params.get('rtl')) ||
					['ar', 'he'].includes(
						(params.get('lang') || 'en-US').split('-')[0],
					) ||
					defaults.rtl,
				userId: params.get('userID') || params.get('userId') || defaults.userId,
				startingSlide:
					Number(params.get('startingSlide')) || defaults.startingSlide,
			},
		};
	}

	return defaults;
};

const URLParamsProvider: React.FC<React.PropsWithChildren<unknown>> = ({
	children,
}) => {
	const [value, setValue] = React.useState(getInitialParams());
	const {query} = useRouter();

	useEffect(() => {
		setValue({
			...value,
			campaign: (query.campaign as string) || defaults.campaign,
		});
	}, [query]);

	return (
		<URLParamsContext.Provider value={value}>
			{children}
		</URLParamsContext.Provider>
	);
};

export default URLParamsProvider;

export const useURLParams = (): URLParamsProviderValue => {
	const urlParamsContext = React.useContext(URLParamsContext);

	if (!urlParamsContext) {
		throw new Error('useURLParams must be used whithin a URLParamsContext');
	}

	return urlParamsContext;
};
